import type { FC } from 'react'
import type { RewardCategoryWithRewards, CreateRewardCategoryDTO } from 'types/rewards'
import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  useTheme,
} from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import { TextField } from 'components/@extended/TextField'
import { useCallback, useState } from 'react'

interface CategorySelectorProps {
  value: string
  onChange: (categoryId: string) => void
  categories: RewardCategoryWithRewards[] | null
  isLoading: boolean
  onCreateCategory: (dto: CreateRewardCategoryDTO) => Promise<void>
  organizationId: string
  disabled?: boolean
}

const CategorySelector: FC<CategorySelectorProps> = ({
  value,
  onChange,
  categories,
  isLoading,
  onCreateCategory,
  organizationId,
  disabled = false,
}) => {
  const theme = useTheme()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryOrder, setNewCategoryOrder] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpenDialog = useCallback(() => {
    // Set default order to be after the last category
    setNewCategoryOrder(categories ? categories.length : 0)
    setDialogOpen(true)
  }, [categories])

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false)
    setNewCategoryName('')
    setNewCategoryOrder(0)
  }, [])

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedValue = event.target.value
      if (selectedValue === '__create_new__') {
        handleOpenDialog()
      } else {
        onChange(selectedValue)
      }
    },
    [onChange, handleOpenDialog]
  )

  const handleCreateCategory = useCallback(async () => {
    if (!newCategoryName.trim() || !organizationId) return

    setIsSubmitting(true)
    try {
      const dto: CreateRewardCategoryDTO = {
        rootOrganizationId: organizationId,
        organizationId: organizationId,
        name: newCategoryName.trim(),
        displayOrder: newCategoryOrder,
      }
      await onCreateCategory(dto)
      handleCloseDialog()
    } finally {
      setIsSubmitting(false)
    }
  }, [newCategoryName, newCategoryOrder, organizationId, onCreateCategory, handleCloseDialog])

  const isValid = newCategoryName.trim() !== ''

  return (
    <>
      <FormControl variant="outlined" fullWidth>
        <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
          Category *
        </InputLabel>
        <Select
          value={value}
          onChange={handleSelectChange}
          variant="outlined"
          style={{ borderRadius: '21px' }}
          displayEmpty
          disabled={disabled || isLoading}
        >
          <MenuItem value="" disabled>
            Select a category
          </MenuItem>
          {categories?.map((category) => (
            <MenuItem key={category.id!} value={category.id!}>
              {category.name}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem value="__create_new__">
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PlusOutlined />
            </ListItemIcon>
            <ListItemText primary="Create New Category" />
          </MenuItem>
        </Select>
      </FormControl>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            variant="outlined"
            pill
            shrink
            fullWidth
            disabled={isSubmitting}
            required
          />
          <TextField
            margin="dense"
            label="Display Order"
            type="number"
            value={newCategoryOrder}
            onChange={(e) => setNewCategoryOrder(parseInt(e.target.value, 10) || 0)}
            variant="outlined"
            pill
            shrink
            fullWidth
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button variant="contained" color="warning" onClick={handleCloseDialog} disabled={isSubmitting}>
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateCategory}
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </AnimateButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CategorySelector
